import type { DashboardTab } from "@/types/dashboard";
import type { AccountTab } from "@/types/account";

/** Leaf dashboard panels (unchanged IDs for capability + API compatibility). */
export const DASHBOARD_PANEL_META: Record<
  DashboardTab,
  { label: string; description: string; badge?: "live" | "leads" | "deploy" }
> = {
  home: { label: "Overview", description: "Site health, traffic, and key metrics." },
  "request-log": { label: "Request log", description: "Live and historical HTTP traffic for this site." },
  "audit-log": { label: "Audit log", description: "Operator and system actions on this site." },
  compliance: { label: "Compliance", description: "SOC 2 control status and evidence for this marketing site." },
  cache: { label: "Cache", description: "Disk and memory cache footprint for this site." },
  preload: { label: "Preload", description: "Sitemap crawl and cache warm-up progress." },
  "live-users": { label: "Live visitors", description: "Visitors currently on the site.", badge: "live" },
  recordings: { label: "Recordings", description: "Session recordings for replay." },
  cookies: { label: "Cookies", description: "Cookies observed on responses." },
  forms: { label: "Forms", description: "Detected forms and protection settings." },
  leads: { label: "Leads", description: "Captured leads and follow-up workflow.", badge: "leads" },
  redirects: { label: "Redirects", description: "Manual and detected redirect rules." },
  queue: { label: "Origin queue", description: "Requests held while the origin is unavailable." },
  "site-structure": { label: "Pages", description: "Site tree, editor, templates, and page deploy." },
  "ab-tests": { label: "Experiments", description: "A/B tests and experiment variants." },
  deploy: { label: "Deploy", description: "Full-site and page deployments.", badge: "deploy" },
  billing: { label: "Plan usage", description: "Plan credits and category usage for this site." },
  firewall: { label: "Firewall", description: "Bans, rate limits, and security events." },
  settings: { label: "Site settings", description: "Origin, cache, tracking, deploy, and integrations." }
};

/**
 * Primary nav section IDs. Prefer one panel per section so features are one click;
 * only group panels that operators always use together.
 */
export type NavSectionId =
  | "home"
  | "live-users"
  | "request-log"
  | "recordings"
  | "leads"
  | "pages"
  | "experiments"
  | "redirects"
  | "deploy"
  | "cache"
  | "firewall"
  | "audit-log"
  | "compliance"
  | "settings";

export type NavSection = {
  id: NavSectionId;
  label: string;
  purpose: string;
  panels: DashboardTab[];
  /** Shown on the primary item when any child has this badge type active. */
  badgeFrom?: Array<"live" | "leads" | "deploy">;
};

/**
 * Straightforward primary navigation (~13 tabs). Leaf panel IDs stay stable for
 * hashes, capabilities, assistant navigation, and deep links.
 *
 * Grouping is intentional and minimal:
 * - Request log + Cookies (traffic inspection)
 * - Leads + Forms (capture pipeline)
 * - Deploy + Preload + Origin queue (publish / warm / drain)
 * - Settings + Plan usage (site config)
 */
export const DASHBOARD_NAV_SECTIONS: NavSection[] = [
  {
    id: "home",
    label: "Home",
    purpose: "Operational overview for the active site.",
    panels: ["home"]
  },
  {
    id: "live-users",
    label: "Live visitors",
    purpose: "Watch visitors on the site in real time.",
    panels: ["live-users"],
    badgeFrom: ["live"]
  },
  {
    id: "request-log",
    label: "Request log",
    purpose: "Inspect HTTP traffic and response cookies.",
    panels: ["request-log", "cookies"]
  },
  {
    id: "recordings",
    label: "Recordings",
    purpose: "Replay recorded visitor sessions.",
    panels: ["recordings"]
  },
  {
    id: "leads",
    label: "Leads",
    purpose: "Work captured leads and form protection.",
    panels: ["leads", "forms"],
    badgeFrom: ["leads"]
  },
  {
    id: "pages",
    label: "Pages",
    purpose: "Browse and edit the site tree.",
    panels: ["site-structure"]
  },
  {
    id: "experiments",
    label: "Experiments",
    purpose: "Run A/B tests and page variants.",
    panels: ["ab-tests"]
  },
  {
    id: "redirects",
    label: "Redirects",
    purpose: "Manage redirect rules.",
    panels: ["redirects"]
  },
  {
    id: "deploy",
    label: "Deploy",
    purpose: "Publish, warm the cache, and drain the origin queue.",
    panels: ["deploy", "preload", "queue"],
    badgeFrom: ["deploy"]
  },
  {
    id: "cache",
    label: "Cache",
    purpose: "Disk and memory cache usage for this site.",
    panels: ["cache"]
  },
  {
    id: "firewall",
    label: "Firewall",
    purpose: "Bans, rate limits, and security events.",
    panels: ["firewall"]
  },
  {
    id: "audit-log",
    label: "Audit log",
    purpose: "Operator and system actions on this site.",
    panels: ["audit-log"]
  },
  {
    id: "compliance",
    label: "Compliance",
    purpose: "SOC 2 control status and evidence for this marketing site.",
    panels: ["compliance"]
  },
  {
    id: "settings",
    label: "Settings",
    purpose: "Configure the site and review plan usage.",
    panels: ["settings", "billing"]
  }
];

/** Legacy / alternate hash values → canonical DashboardTab. */
export const DASHBOARD_HASH_ALIASES: Record<string, DashboardTab> = {
  overview: "home",
  pages: "site-structure",
  "site-structure": "site-structure",
  "site_structure": "site-structure",
  experiments: "ab-tests",
  "ab-tests": "ab-tests",
  "ab_tests": "ab-tests",
  "live-visitors": "live-users",
  "live_users": "live-users",
  "site-usage": "cache",
  usage: "billing",
  "plan-usage": "billing",
  "origin-queue": "queue",
  configuration: "settings",
  "site-settings": "settings",
  soc2: "compliance",
  "soc-2": "compliance"
};

export const ALL_DASHBOARD_TABS: DashboardTab[] = DASHBOARD_NAV_SECTIONS.flatMap((s) => s.panels);

export function sectionForTab(tab: DashboardTab): NavSection {
  return DASHBOARD_NAV_SECTIONS.find((s) => s.panels.includes(tab)) || DASHBOARD_NAV_SECTIONS[0];
}

export function resolveDashboardHash(raw: string): DashboardTab {
  const h = String(raw || "").replace(/^#/, "").trim().toLowerCase();
  if (!h) return "home";
  if (DASHBOARD_HASH_ALIASES[h]) return DASHBOARD_HASH_ALIASES[h];
  if ((ALL_DASHBOARD_TABS as string[]).includes(h)) return h as DashboardTab;
  return "home";
}

export function defaultPanelForSection(section: NavSection, canUse: (tab: DashboardTab) => boolean): DashboardTab {
  for (const panel of section.panels) {
    if (canUse(panel)) return panel;
  }
  return section.panels[0];
}

/* ── Account console ─────────────────────────────────────────────────────── */

export type AccountNavSectionId =
  | "sites"
  | "profile"
  | "billing"
  | "users"
  | "plans"
  | "credit-rates"
  | "global-settings"
  | "settings-skeletons";

export type AccountNavSection = {
  id: AccountNavSectionId;
  label: string;
  purpose: string;
  panels: AccountTab[];
  adminOnly?: boolean;
};

export const ACCOUNT_PANEL_META: Record<AccountTab, { label: string; description: string }> = {
  sites: { label: "Sites", description: "Add, open, and manage websites on this account." },
  profile: { label: "Profile", description: "Your email, password, and authenticator MFA." },
  billing: { label: "Billing", description: "Usage this month, current plans, and available pricing." },
  users: { label: "Users", description: "Invite operators and control site access." },
  "global-settings": {
    label: "Global settings",
    description: "Platform-wide assistant, email, Slack, and provisioning defaults."
  },
  "settings-skeletons": {
    label: "Settings presets",
    description: "Reusable settings patches applied when provisioning new sites."
  },
  plans: { label: "Plans", description: "Plan catalog, capabilities, and per-site assignments." },
  "credit-rates": { label: "Credit rates", description: "Global credit pricing by usage category." }
};

/** Flat account nav — each feature is a primary tab (no secondary nesting). */
export const ACCOUNT_NAV_SECTIONS: AccountNavSection[] = [
  {
    id: "sites",
    label: "Sites",
    purpose: "Choose a website or start onboarding.",
    panels: ["sites"]
  },
  {
    id: "profile",
    label: "Profile",
    purpose: "Update your account details and security.",
    panels: ["profile"]
  },
  {
    id: "billing",
    label: "Billing",
    purpose: "Usage, invoices-ready totals, and plan changes.",
    panels: ["billing"]
  },
  {
    id: "users",
    label: "Users",
    purpose: "Manage users and site access.",
    panels: ["users"],
    adminOnly: true
  },
  {
    id: "plans",
    label: "Plans",
    purpose: "Plan catalog and per-site assignments.",
    panels: ["plans"],
    adminOnly: true
  },
  {
    id: "credit-rates",
    label: "Credit rates",
    purpose: "Global credit pricing by usage category.",
    panels: ["credit-rates"],
    adminOnly: true
  },
  {
    id: "global-settings",
    label: "Global settings",
    purpose: "Account-wide assistant, email, Slack, and defaults.",
    panels: ["global-settings"],
    adminOnly: true
  },
  {
    id: "settings-skeletons",
    label: "Settings presets",
    purpose: "Provisioning presets for new sites.",
    panels: ["settings-skeletons"],
    adminOnly: true
  }
];

export const ACCOUNT_HASH_ALIASES: Record<string, AccountTab> = {
  settings: "global-settings",
  "global-settings": "global-settings",
  team: "users",
  users: "users",
  account: "profile",
  security: "profile",
  mfa: "profile",
  rates: "credit-rates",
  "credit-rates": "credit-rates",
  "settings-presets": "settings-skeletons",
  skeletons: "settings-skeletons",
  platform: "global-settings"
};

export function sectionForAccountTab(tab: AccountTab): AccountNavSection {
  return ACCOUNT_NAV_SECTIONS.find((s) => s.panels.includes(tab)) || ACCOUNT_NAV_SECTIONS[0];
}

export function resolveAccountHash(raw: string): AccountTab {
  const h = String(raw || "").replace(/^#/, "").trim().toLowerCase();
  if (!h) return "sites";
  if (ACCOUNT_HASH_ALIASES[h]) return ACCOUNT_HASH_ALIASES[h];
  const valid: AccountTab[] = [
    "sites",
    "profile",
    "billing",
    "users",
    "global-settings",
    "plans",
    "credit-rates",
    "settings-skeletons"
  ];
  return valid.includes(h as AccountTab) ? (h as AccountTab) : "sites";
}
